import { API } from '@/config';
import type {
  CurrentUser,
  LoginRequest,
  LoginResponse,
  MfaEnrollment,
  MfaStatus,
  MfaVerifyRequest,
  MfaVerifyResponse,
} from '@/types';
import { api } from './client';
import { clearTokens, isSessionLikely, markSessionLikely, setAccessToken } from './tokens';

/**
 * Authentication calls.
 *
 * A thin layer over the client, kept separate so components never build URLs
 * or know which endpoint does what.
 */
export const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const result = await api.post<LoginResponse>(API.endpoints.login, credentials, {
      anonymous: true,
    });

    // The response is a union: an access token, or a demand for a second factor.
    // Narrowing here means callers cannot read accessToken off an MFA result.
    if ('accessToken' in result) {
      setAccessToken(result.accessToken);
      // The refresh token arrived as an httpOnly cookie the browser has already
      // stored; all we record is that it exists.
      markSessionLikely();
    }

    return result;
  },

  /**
   * Completes a login that stopped at the second factor.
   *
   * Anonymous, like login itself: this is what ISSUES the token, so it cannot
   * require one. The access token is stored here for the same reason it is in
   * login - the refresh half arrived as an httpOnly cookie the browser has
   * already put away, and all this side records is that it exists.
   */
  async verifyMfa(body: MfaVerifyRequest): Promise<MfaVerifyResponse> {
    const result = await api.post<MfaVerifyResponse>(API.endpoints.mfaVerify, body, {
      anonymous: true,
    });

    setAccessToken(result.accessToken);
    markSessionLikely();
    return result;
  },

  mfaStatus(signal?: AbortSignal): Promise<MfaStatus> {
    return api.get<MfaStatus>(API.endpoints.mfa, signal ? { signal } : {});
  },

  /**
   * Starts an enrolment. The secret comes back ONCE.
   *
   * There is no way to ask for it again: the server keeps only a sealed copy,
   * so a lost secret means starting over. That is the correct trade, and the UI
   * says so rather than implying the QR code can be reopened later.
   */
  beginMfaEnrollment(): Promise<MfaEnrollment> {
    return api.post<MfaEnrollment>(API.endpoints.mfaEnroll);
  },

  confirmMfaEnrollment(code: string): Promise<{ status: string }> {
    return api.post<{ status: string }>(API.endpoints.mfaEnrollConfirm, { code });
  },

  /** Requires a current code even though the caller is signed in. */
  disableMfa(code: string): Promise<{ status: string }> {
    return api.post<{ status: string }>(API.endpoints.mfaDisable, { code });
  },

  me(signal?: AbortSignal): Promise<CurrentUser> {
    return api.get<CurrentUser>(API.endpoints.me, signal ? { signal } : {});
  },

  async logout(): Promise<void> {
    try {
      await api.post(API.endpoints.logout);
    } finally {
      /*
       * Cleared even if the call fails. A network error must not leave someone
       * apparently signed in - locally forgetting the token is the part that
       * actually matters to them.
       */
      clearTokens();
    }
  },

  /**
   * Whether a refresh is worth attempting on boot.
   *
   * A guess, and it has to be: the refresh cookie is httpOnly, so this side
   * genuinely cannot see whether a session exists. The server is the only
   * authority - this just avoids a certain-to-fail request for somebody who has
   * never signed in.
   */
  hasStoredSession(): boolean {
    return isSessionLikely();
  },
};
